'use client';

import { useState } from 'react';
import { X, Loader2, Save, Lock, History } from 'lucide-react';
import { toast } from 'sonner';
import { TradeCardData } from './TradeCard';
import { createClient } from '@/lib/supabase/client';
import { useLang } from '@/lib/i18n/LanguageContext';

interface TradeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: TradeCardData;
  onTradeUpdated: (updated: Partial<TradeCardData>) => void;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
  } catch {}
  return {};
}

function fmt(n?: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

// "" → null, "1,2345" → 1.2345, garbage → NaN (caught by validation)
function num(s: string): number | null {
  const v = s.trim();
  if (!v) return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

// Post-publish edit rules (owner rule 2026-09-02, transparency-first):
//   • caption / commentary — always editable; posts show an "edited" label
//   • SL / TP1 / TP2 / TP3 — editable ONLY while the signal is live (OPEN or PENDING). Every change
//     is written to a public ledger (old → new, timestamp, optional reason) and the settlement
//     engine applies the new level only from the moment of the change — nothing is rewritten.
//   • ticker / direction / entry / timeframe — never (DB trigger backstop).
export default function TradeEditModal({ isOpen, onClose, trade, onTradeUpdated }: TradeEditModalProps) {
  const { t } = useLang();
  const canEditLevels =
    (trade.ideaType ?? 'SIGNAL') === 'SIGNAL' && (trade.status === 'OPEN' || trade.status === 'PENDING');

  const [caption, setCaption] = useState(trade.caption || '');
  const [stopLoss, setStopLoss] = useState(trade.stopLoss != null ? String(trade.stopLoss) : '');
  const [tp1, setTp1] = useState(trade.takeProfit1 != null ? String(trade.takeProfit1) : '');
  const [tp2, setTp2] = useState(trade.takeProfit2 != null ? String(trade.takeProfit2) : '');
  const [tp3, setTp3] = useState(trade.takeProfit3 != null ? String(trade.takeProfit3) : '');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const isBuy = trade.direction === 'BUY';

  const levelsChanged = canEditLevels && (
    num(stopLoss) !== (trade.stopLoss ?? null) ||
    num(tp1) !== (trade.takeProfit1 ?? null) ||
    num(tp2) !== (trade.takeProfit2 ?? null) ||
    num(tp3) !== (trade.takeProfit3 ?? null)
  );
  const captionChanged = (caption.trim() || null) !== ((trade.caption || '').trim() || null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!captionChanged && !levelsChanged) { onClose(); return; }

    const body: Record<string, unknown> = {};
    if (captionChanged) body.caption = caption.trim() || null;

    if (levelsChanged) {
      const sl = num(stopLoss), t1 = num(tp1), t2 = num(tp2), t3 = num(tp3);
      if (sl == null || Number.isNaN(sl) || t1 == null || Number.isNaN(t1) || Number.isNaN(t2) || Number.isNaN(t3)) {
        toast.error(t('Enter valid Stop Loss and Take Profit 1 prices'));
        return;
      }
      // Client-side mirror of the DB rule so the trader gets the message before a round-trip;
      // update_trade_levels() re-validates server-side regardless.
      const entry = trade.entryPrice;
      const wrongSide = isBuy
        ? (sl >= entry || t1 <= entry || (t2 != null && t2 <= entry) || (t3 != null && t3 <= entry))
        : (sl <= entry || t1 >= entry || (t2 != null && t2 >= entry) || (t3 != null && t3 >= entry));
      if (wrongSide) {
        toast.error(isBuy
          ? t('For a LONG the Stop Loss must be below and every Take Profit above the entry')
          : t('For a SHORT the Stop Loss must be above and every Take Profit below the entry'));
        return;
      }
      body.stopLoss = sl;
      body.takeProfit1 = t1;
      body.takeProfit2 = t2;
      body.takeProfit3 = t3;
      body.note = note.trim() || null;
    }

    setLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${trade.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const result = await res.json();
      if (!res.ok) {
        toast.error(t(result.error || 'Failed to update signal'));
        return;
      }

      toast.success(levelsChanged
        ? t('Levels updated — the change is now visible in the signal history')
        : t('Analysis updated'));

      const row = result.trade || {};
      onTradeUpdated({
        ...(captionChanged ? { caption: caption.trim(), editedAt: row.editedAt || new Date().toISOString() } : {}),
        ...(levelsChanged ? {
          stopLoss: row.stopLoss ?? (body.stopLoss as number),
          takeProfit1: row.takeProfit1 ?? (body.takeProfit1 as number),
          takeProfit2: row.takeProfit2 ?? undefined,
          takeProfit3: row.takeProfit3 ?? undefined,
          revisions: result.revisions ?? trade.revisions,
          revisionCount: result.revisions?.length ?? ((trade.revisionCount ?? 0) + 1),
        } : {}),
      });
      onClose();
    } catch {
      toast.error(t('Network error — please try again'));
    } finally {
      setLoading(false);
    }
  }

  const lockedRows: { label: string; value: string; color?: string }[] = [
    { label: 'Ticker', value: trade.ticker, color: 'var(--text-primary)' },
    { label: 'Direction', value: isBuy ? '▲ LONG' : '▼ SHORT', color: isBuy ? 'var(--bullish)' : 'var(--bearish)' },
    { label: 'Entry', value: fmt(trade.entryPrice), color: 'var(--win)' },
    // Settled signals: the levels that produced the result are frozen too.
    ...(!canEditLevels ? [
      { label: 'Stop Loss', value: fmt(trade.stopLoss), color: 'var(--bearish)' },
      { label: 'Take Profit', value: fmt(trade.takeProfit1), color: 'var(--bullish)' },
    ] : []),
  ];

  const levelFields: { label: string; value: string; set: (v: string) => void; color: string; required: boolean }[] = [
    { label: 'Stop Loss', value: stopLoss, set: setStopLoss, color: 'var(--bearish)', required: true },
    { label: 'Take Profit 1', value: tp1, set: setTp1, color: 'var(--bullish)', required: true },
    { label: 'Take Profit 2', value: tp2, set: setTp2, color: 'var(--bullish)', required: false },
    { label: 'Take Profit 3', value: tp3, set: setTp3, color: 'var(--bullish)', required: false },
  ];

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: 'rgba(28,43,38,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', boxShadow: '0 24px 64px rgba(28,43,38,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b sticky top-0"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', borderRadius: '16px 16px 0 0' }}
        >
          <div className="flex items-center gap-2">
            <Save size={16} style={{ color: 'var(--accent)' }} />
            <h2 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
              {canEditLevels ? t('Edit signal') : t('Edit Analysis')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors duration-100"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; }}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          {/* Locked trade values */}
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
              <Lock size={11} style={{ color: 'var(--text-muted)' }} />
              <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                {t("Locked — verified entry, can't be changed")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-3">
              {lockedRows.map((r) => (
                <div key={r.label} className="flex items-center justify-between gap-2">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t(r.label)}</span>
                  <span className="text-xs font-semibold font-mono" style={{ color: r.color || 'var(--text-primary)' }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Editable levels — live signals only, every change is public */}
          {canEditLevels && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
                {t('LEVELS (live signal — changes are public)')}
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                {levelFields.map((f) => (
                  <div key={f.label}>
                    <span className="block text-[11px] mb-1" style={{ color: f.color, fontWeight: 700 }}>
                      {f.label}{!f.required && <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> · {t('optional')}</span>}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={f.value}
                      onChange={(e) => f.set(e.target.value)}
                      placeholder={f.required ? '' : '—'}
                      className="input-base w-full px-3 py-2 text-sm font-mono-nums"
                    />
                  </div>
                ))}
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('Reason for the change (optional, shown publicly)')}
                rows={2}
                maxLength={500}
                className="input-base w-full px-3 py-2 text-sm resize-none mt-2.5"
              />
              <p className="flex items-start gap-1.5 text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                <History size={12} className="shrink-0 mt-0.5" />
                <span>{t('Every SL/TP change is recorded with the old and new value and shown publicly on the signal. Entry, direction and ticker stay locked.')}</span>
              </p>
            </div>
          )}

          {/* Editable caption */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
              {t('CAPTION / COMMENTARY')}
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t('Update your thesis or commentary…')}
              rows={canEditLevels ? 3 : 5}
              autoFocus={!canEditLevels}
              className="input-base w-full px-3 py-2.5 text-sm resize-none"
            />
            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
              {t('You can refine the write-up anytime. Edited posts show an “edited” label for transparency.')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2.5 text-sm flex-1">
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || (!captionChanged && !levelsChanged)}
              className="btn-primary px-4 py-2.5 text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (<><Loader2 size={13} className="animate-spin" /> {t('Saving…')}</>) : (<><Save size={13} /> {t('Save Changes')}</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
